import { emptyStepState, markStep, mergeStepState, normalizeTerm, pickCloze, studyUnits, tokenize, type ClozeBlank, type StepState } from '@engpod/core';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { Icon } from '../components/Icon';
import { Sentence } from '../components/Sentence';
import { WordSheet } from '../components/WordSheet';
import { absolute, api, cached, errorText } from '../lib/api';
import { useAuth } from '../lib/auth';
import { prefs, readLocal, writeLocal } from '../lib/local';
import { usePreloadedMedia, useRangePlayer } from '../lib/useRangePlayer';
import type { EpisodeDetail, Program, Progress, SegmentView } from '../types';

type Phase = 'listen' | 'cloze' | 'reveal';
type Rating = 'ok' | 'hard' | 'lost';
const AUTO_LOOPS = 3;
/** Sentences per section; a short break card appears between sections. */
const CHUNK = 8;

function compare(answer: string, target: string) {
  const want = normalizeTerm(target).split(' ');
  const got = new Set(normalizeTerm(answer).split(' '));
  return want.map((w) => ({ w, ok: got.has(w) }));
}

/**
 * 精听 — the core of the app, on its own page.
 * Audio-only by default (≈3 MB, downloaded once so every jump is instant); video optional.
 * One study unit at a time with big prev / replay / next buttons and swipe.
 */
export function ListenPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const { me } = useAuth();
  const [detail, setDetail] = useState<EpisodeDetail | null>(null);
  const [programTitle, setProgramTitle] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [state, setState] = useState<StepState>(() => readLocal(id).stepState ?? emptyStepState());

  useEffect(() => {
    const ep = cached<EpisodeDetail>(`/episodes/${id}`);
    if (ep.snapshot) setDetail(ep.snapshot);
    ep.fresh.then(({ data, changed }) => changed && setDetail(data), (e) => !ep.snapshot && setError(errorText(e)));
    const title = (d: { programs: Program[] }) => setProgramTitle(d.programs.find((p) => p.episodes.some((e) => e.id === id))?.title ?? '我的资料');
    const pg = cached<{ programs: Program[] }>('/programs');
    if (pg.snapshot) title(pg.snapshot);
    pg.fresh.then(({ data }) => title(data), () => undefined);
    if (me?.user) {
      const apply = ({ progress }: { progress: Progress[] }) => {
        const p = progress.find((x) => x.episodeId === id);
        if (p) setState((s) => mergeStepState(s, p.stepState));
      };
      const pr = cached<{ progress: Progress[] }>('/progress');
      if (pr.snapshot) apply(pr.snapshot);
      pr.fresh.then(({ data }) => apply(data), () => undefined);
    }
  }, [id, me?.user]);

  const save = useCallback(
    (next: StepState) => {
      writeLocal(id, { stepState: next });
      if (me?.user) void api(`/progress/${id}`, { method: 'PUT', json: { stepState: next } }).catch(() => undefined);
    },
    [id, me?.user],
  );
  const update = useCallback(
    (patch: Partial<StepState>) =>
      setState((s) => {
        const next = { ...s, ...patch };
        save(next);
        return next;
      }),
    [save],
  );

  if (error) return <p className="page status">{error}</p>;
  if (!detail) return <p className="page muted">加载中…</p>;
  if (detail.episode.media.kind !== 'file') {
    return (
      <div className="page">
        <p>{detail.episode.media.kind === 'locked' ? '请先到「我的」登录后再精听。' : '这一集还没有音频。'}</p>
        <button onClick={() => navigate(`/?ep=${id}`)}>返回</button>
      </div>
    );
  }
  return (
    <Listener
      detail={detail}
      programTitle={programTitle}
      state={state}
      update={update}
      onFinish={() => {
        setState((s) => {
          const next = markStep(s, 'intensive');
          save(next);
          return next;
        });
        navigate(`/?ep=${id}`);
      }}
      onBack={() => navigate(`/?ep=${id}`)}
    />
  );
}

function Listener({ detail, programTitle, state, update, onFinish, onBack }: { detail: EpisodeDetail; programTitle: string; state: StepState; update(p: Partial<StepState>): void; onFinish(): void; onBack(): void }) {
  const ep = detail.episode;
  const media = ep.media.kind === 'file' ? ep.media : null;
  const hasVideo = ep.mediaType === 'video';
  const [videoMode, setVideoMode] = useState(() => hasVideo && prefs.get<boolean>('listenVideo', false));
  const audioUrl = media?.audioUrl ? absolute(media.audioUrl) : null;
  // Audio is downloaded completely for instant jumps; video streams.
  const pre = usePreloadedMedia(!videoMode ? (audioUrl ?? absolute(media?.url)) : null);
  const src = videoMode ? absolute(media?.url) : pre.src;
  const player = useRangePlayer();

  const units = useMemo(() => studyUnits(detail.segments), [detail.segments]);
  const pool = useMemo(() => detail.segments.flatMap((s) => tokenize(s.en).flatMap((t) => (t.word ? [t.word] : []))), [detail.segments]);
  const exprWords = useMemo(() => detail.lesson.expressions.map((e) => e.text), [detail.lesson.expressions]);
  const [i, setI] = useState(() => Math.min(state.intensiveReached ?? 0, Math.max(0, units.length - 1)));
  const [phase, setPhase] = useState<Phase>('listen');
  const [loop, setLoopState] = useState(() => prefs.get<boolean>('listenLoop', true));
  const [auto, setAuto] = useState(false);
  const [started, setStarted] = useState(false);
  const [filled, setFilled] = useState<Record<number, string>>({});
  const [wrong, setWrong] = useState<string | null>(null);
  const [showZh, setShowZh] = useState(false);
  const [dictation, setDictation] = useState<string | null>(null);
  const [listOpen, setListOpen] = useState(false);
  const [listZh, setListZh] = useState(() => prefs.get<boolean>('listZh', false));
  const listRef = useRef<HTMLOListElement>(null);
  const [reviewHard, setReviewHard] = useState(false);
  const [word, setWord] = useState<{ seg: SegmentView; token: number } | null>(null);
  const touch = useRef<{ x: number; y: number } | null>(null);
  const [lite, setLite] = useState(() => prefs.get<boolean>('listenLite', false));
  const [pause, setPause] = useState<number | null>(null); // index of the next unit after a finished section
  const sessionStart = useRef(Date.now());
  const [, tickMinute] = useState(0);
  useEffect(() => {
    const t = setInterval(() => tickMinute((n) => n + 1), 30_000);
    return () => clearInterval(t);
  }, []);
  const minutes = Math.round((Date.now() - sessionStart.current) / 60_000);

  // Long sessions: keep the screen on while audio plays (iOS 16.4+).
  useEffect(() => {
    if (!player.playing || !('wakeLock' in navigator)) return;
    let lock: { release(): Promise<void> } | null = null;
    void (navigator as Navigator & { wakeLock: { request(t: 'screen'): Promise<{ release(): Promise<void> }> } }).wakeLock
      .request('screen')
      .then((l) => (lock = l), () => undefined);
    return () => void lock?.release();
  }, [player.playing]);

  const hardSet = new Set(state.hardSegments ?? []);
  const order = useMemo(() => {
    const all = units.map((_, n) => n);
    return reviewHard ? all.filter((n) => units[n]!.segmentIds.some((sid) => hardSet.has(sid))) : all;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [units, reviewHard]);
  const unit = units[i];
  const tokens = useMemo(() => (unit ? tokenize(unit.en) : []), [unit]);
  const blanks: ClozeBlank[] = useMemo(() => (unit ? pickCloze(tokens, { prefer: exprWords, pool, seed: i + 7 }) : []), [unit, tokens, exprWords, pool, i]);
  const activeBlank = blanks.find((b) => !filled[b.token]);

  const playUnit = useCallback(
    (n: number, asLoop = loop || auto) => {
      const u = units[n];
      if (u) player.playRange(u.startMs, u.endMs, asLoop);
    },
    [units, loop, auto, player],
  );

  const goTo = (n: number, play = true) => {
    const next = Math.max(0, Math.min(units.length - 1, n));
    setI(next);
    setPhase('listen');
    setFilled({});
    setWrong(null);
    setShowZh(false);
    setDictation(null);
    if (next > (state.intensiveReached ?? 0)) update({ intensiveReached: next });
    if (play) playUnit(next);
  };
  const step = (dir: 1 | -1) => {
    if (pause !== null) {
      const n = pause;
      setPause(null);
      return goTo(dir > 0 ? n : n - 1);
    }
    const pos = order.indexOf(i);
    const target = pos === -1 ? order.find((n) => (dir > 0 ? n > i : n < i)) : order[pos + dir];
    if (target === undefined) return dir > 0 ? onFinish() : undefined;
    if (dir > 0 && !reviewHard && !auto && target % CHUNK === 0) {
      player.stop();
      setPause(target);
      if (target > (state.intensiveReached ?? 0)) update({ intensiveReached: target });
      return;
    }
    goTo(target);
  };

  // The sentence list follows the current sentence (scrolling only the list itself).
  useEffect(() => {
    const list = listRef.current;
    const row = list?.querySelector<HTMLElement>(`[data-n="${i}"]`);
    if (list && row) list.scrollTo({ top: row.offsetTop - list.clientHeight / 2 + row.clientHeight / 2, behavior: 'smooth' });
  }, [i, listOpen]);

  // Hands-free mode: after N passes, move on by itself.
  useEffect(() => {
    if (auto && player.loops > AUTO_LOOPS) step(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [player.loops, auto]);

  const rateUnit = (r: Rating) => {
    if (!unit) return;
    const set = new Set(state.hardSegments ?? []);
    for (const sid of unit.segmentIds) (r === 'ok' ? set.delete(sid) : set.add(sid));
    update({ hardSegments: [...set] });
    step(1);
  };
  const pick = (blank: ClozeBlank, option: string) => {
    if (option === blank.answer) {
      const next = { ...filled, [blank.token]: option };
      setFilled(next);
      if (blanks.every((b) => next[b.token])) setTimeout(() => setPhase('reveal'), 450);
    } else {
      setWrong(option);
      setTimeout(() => setWrong(null), 650);
    }
  };
  const openUnitWord = (t: number) => {
    if (!unit) return;
    player.stop();
    const text = tokens[t]?.text ?? '';
    const seg = detail.segments.find((s) => unit.segmentIds.includes(s.id) && tokenize(s.en).some((x) => x.text === text)) ?? detail.segments.find((s) => s.id === unit.segmentIds[0]);
    if (seg) setWord({ seg, token: Math.max(0, tokenize(seg.en).findIndex((x) => x.text === text)) });
  };

  if (!unit) return <p className="page muted">这一集还没有字幕。</p>;
  const ready = Boolean(src);
  const hardCount = units.filter((u) => u.segmentIds.some((sid) => hardSet.has(sid))).length;
  const sections = Math.ceil(units.length / CHUNK);
  const section = Math.floor(i / CHUNK) + 1;
  const toListen = () => (lite || !blanks.length ? 'reveal' : 'cloze');

  return (
    <div className="listen-page">
      <header className="lp-head">
        <button className="plain lp-back" onClick={onBack} aria-label="返回">
          ‹
        </button>
        <div className="lp-title">
          <b>精听</b>
          <small>
            {programTitle} · 第 {section}/{sections} 段{minutes > 0 ? ` · 已练 ${minutes} 分钟` : ''}
          </small>
        </div>
        {hasVideo && (
          <div className="media-mode" role="group" aria-label="音频或视频">
            <button
              className={videoMode ? '' : 'active'}
              onClick={() => {
                setVideoMode(false);
                prefs.set('listenVideo', false);
                setStarted(false);
              }}
            >
              音频
            </button>
            <button
              className={videoMode ? 'active' : ''}
              onClick={() => {
                setVideoMode(true);
                prefs.set('listenVideo', true);
                setStarted(false);
              }}
            >
              视频
            </button>
          </div>
        )}
      </header>

      <section className={`lp-media${videoMode ? ' video' : ''}`}>
        <video key={videoMode ? 'v' : 'a'} {...player.bind} src={src ?? undefined} playsInline preload="auto" poster={videoMode ? absolute(ep.posterUrl) : undefined} hidden={!videoMode} />
        {!videoMode && (
          <div className="lp-audio">
            {ep.posterUrl ? <img src={absolute(ep.posterUrl)} alt="" /> : <div className="lp-cover" />}
            <div>
              <b>{ready ? '音频已就绪 · 跳句即播' : `加载音频 ${Math.round(pre.progress * 100)}%`}</b>
              <small>仅音频模式，约 3 MB，加载一次后上一句 / 下一句无需等待</small>
            </div>
          </div>
        )}
      </section>

      <div className="unit-progress">
        <div className="unit-bar">
          <i style={{ width: `${((i + 1) / units.length) * 100}%` }} />
        </div>
        <small>
          {i + 1} / {units.length}
          {reviewHard ? ' · 难句' : ''}
        </small>
      </div>

      <article
        className="lp-card"
        onTouchStart={(e) => (touch.current = { x: e.touches[0]!.clientX, y: e.touches[0]!.clientY })}
        onTouchEnd={(e) => {
          const t = touch.current;
          touch.current = null;
          if (!t) return;
          const dx = e.changedTouches[0]!.clientX - t.x;
          const dy = e.changedTouches[0]!.clientY - t.y;
          if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy) * 1.5) step(dx < 0 ? 1 : -1);
        }}
      >
        <div className="unit-top">
          <span className={`loop-badge${player.playing ? ' live' : ''}`}>{auto ? `连播 · 第 ${Math.min(player.loops, AUTO_LOOPS)}/${AUTO_LOOPS} 遍` : loop ? `循环 · 第 ${player.loops} 遍` : '单次'}</span>
          <div className="row tight">
            <button className="chip" onClick={() => (setStarted(true), playUnit(i))} aria-label="从头重播这一句">
              ⟲ 重播
            </button>
            <button
              className={`chip${loop ? ' on' : ''}`}
              onClick={() => {
                const v = !loop;
                setLoopState(v);
                prefs.set('listenLoop', v);
                player.setLoop(v || auto);
              }}
            >
              循环
            </button>
            <button className={`chip${player.rate < 1 ? ' on' : ''}`} onClick={() => player.setRate(player.rate < 1 ? 1 : 0.75)}>
              0.75×
            </button>
            <button
              className={`chip${lite ? ' on' : ''}`}
              title="精简：不填词，听完直接看原文"
              onClick={() => {
                setLite(!lite);
                prefs.set('listenLite', !lite);
              }}
            >
              精简
            </button>
            <button
              className={`chip${auto ? ' on' : ''}`}
              onClick={() => {
                setAuto(!auto);
                player.setLoop(!auto || loop);
              }}
            >
              免手动
            </button>
          </div>
        </div>

        {pause !== null ? (
          <div className="lp-break">
            <span className="lp-break-icon">☕</span>
            <b>第 {Math.floor((pause - 1) / CHUNK) + 1} 段完成</b>
            <small>
              还剩 {sections - Math.floor((pause - 1) / CHUNK) - 1} 段 · 已练 {minutes} 分钟 · 难句 {hardCount} 句
            </small>
            <div className="row wrap">
              <button
                className="primary"
                onClick={() => {
                  const n = pause;
                  setPause(null);
                  goTo(n);
                }}
              >
                继续下一段
              </button>
              <button onClick={onBack}>歇一下，回首页</button>
            </div>
            <small className="muted">下次进来会从这里继续。</small>
          </div>
        ) : !started ? (
          <button
            className="lp-start"
            disabled={!ready}
            onClick={() => {
              setStarted(true);
              playUnit(i);
            }}
          >
            <Icon name="play" />
            {ready ? (i > 0 ? `从第 ${i + 1} 句继续` : '开始精听') : '音频加载中…'}
          </button>
        ) : phase === 'listen' ? (
          <button className="listen-area" onClick={() => playUnit(i)}>
            <span className="ear">🎧</span>
            <span>先只听，不看字幕</span>
            <small>左右滑动切换句子</small>
          </button>
        ) : phase === 'cloze' ? (
          <div className="cloze">
            <p className="en big">
              {tokens.map((t, k) => {
                const b = blanks.find((x) => x.token === k);
                if (!b) return <span key={k}>{t.text}</span>;
                return (
                  <span key={k} className={`gap${filled[k] ? ' ok' : b === activeBlank ? ' active' : ''}`}>
                    {filled[k] ?? ' '.repeat(Math.max(4, b.answer.length))}
                  </span>
                );
              })}
            </p>
            {activeBlank && (
              <div className="word-bank">
                {activeBlank.options.map((o) => (
                  <button key={o} className={`bank-word${wrong === o ? ' wrong' : ''}`} onClick={() => pick(activeBlank, o)}>
                    {o}
                  </button>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="reveal">
            <p className="en big">
              <Sentence text={unit.en} onWord={openUnitWord} />
            </p>
            {showZh ? (
              <p className="zh">{unit.zh}</p>
            ) : (
              <button className="plain small show-zh" onClick={() => setShowZh(true)}>
                看中文
              </button>
            )}
          </div>
        )}

        {dictation !== null && (
          <div className="dictation">
            <textarea value={dictation} onChange={(e) => setDictation(e.target.value)} placeholder="把听到的整句写下来" rows={2} />
            {phase === 'reveal' && dictation && (
              <p className="diff">
                {compare(dictation, unit.en).map((x, k) => (
                  <span key={k} className={x.ok ? 'ok' : 'miss'}>
                    {x.w}{' '}
                  </span>
                ))}
              </p>
            )}
          </div>
        )}

        {pause === null && started && phase === 'listen' && (
          <div className="row wrap">
            <button className="primary grow" onClick={() => setPhase(toListen())}>
              {toListen() === 'cloze' ? '听好了，填词' : '听好了，看原文'}
            </button>
            <button onClick={() => setPhase('reveal')}>看原文</button>
          </div>
        )}
        {pause === null && phase === 'cloze' && (
          <button className="plain small" onClick={() => setPhase('reveal')}>
            跳过，看原文
          </button>
        )}
        {pause === null && phase === 'reveal' && (
          <div className="rate-row">
            <button className="rate lost" onClick={() => rateUnit('lost')}>
              没听懂
            </button>
            <button className="rate hard" onClick={() => rateUnit('hard')}>
              有点难
            </button>
            <button className="rate ok" onClick={() => rateUnit('ok')}>
              听懂了
            </button>
          </div>
        )}
        <div className="row spread lp-extras">
          <button className="plain small" onClick={() => setDictation(dictation === null ? '' : null)}>
            {dictation === null ? '✎ 键盘听写（可选）' : '收起听写'}
          </button>
          {hardCount > 0 && (
            <button className={`plain small${reviewHard ? ' on-text' : ''}`} onClick={() => setReviewHard(!reviewHard)}>
              {reviewHard ? '显示全部句子' : `只练难句（${hardCount}）`}
            </button>
          )}
        </div>
      </article>

      <section className="lp-list">
        <div className="row spread">
          <button className="plain small" onClick={() => setListOpen(!listOpen)}>
            {listOpen ? '收起全部句子 ▴' : `全部句子（${units.length}）▾`}
          </button>
          {listOpen && (
            <button
              className={`chip${listZh ? ' on' : ''}`}
              onClick={() => {
                setListZh(!listZh);
                prefs.set('listZh', !listZh);
              }}
            >
              中文
            </button>
          )}
        </div>
        {listOpen && (
          <ol ref={listRef}>
            {units.map((u, n) => (
              <li key={u.key} data-n={n} className={`${n === i ? 'current' : ''}${u.segmentIds.some((sid) => hardSet.has(sid)) ? ' hard' : ''}`} onClick={() => (setStarted(true), goTo(n))}>
                <span>{n + 1}</span>
                <div>
                  {u.en}
                  {listZh && u.zh && <small>{u.zh}</small>}
                </div>
              </li>
            ))}
          </ol>
        )}
      </section>

      <footer className="lp-nav">
        <button onClick={() => (setStarted(true), step(-1))} disabled={order.indexOf(i) === 0 || i === 0} aria-label="上一句">
          <span>‹</span>上一句
        </button>
        <button
          className="lp-replay"
          onClick={() => {
            setStarted(true);
            player.toggle(() => playUnit(i));
          }}
          disabled={!ready}
          aria-label={player.playing ? '暂停' : '播放'}
        >
          <Icon name={player.playing ? 'pause' : 'play'} />
        </button>
        <button onClick={() => (setStarted(true), step(1))} aria-label="下一句">
          {i === units.length - 1 ? '完成' : '下一句'}
          <span>›</span>
        </button>
      </footer>

      {word && <WordSheet segment={word.seg} tokenIndex={word.token} episode={ep} programTitle={programTitle} onPlay={() => player.playRange(word.seg.startMs, word.seg.endMs, false)} onClose={() => setWord(null)} />}
    </div>
  );
}
